import moment from "moment";
import { useForm } from "react-hook-form";
import { DevTool } from "@hookform/devtools";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
  Switch,
  Link
} from "@material-ui/core";
import {
  useFinaliseOpportunityMutation,
  useGetOpportunityIssueWeeksQuery
} from "@bonliva-crm/graphql";
import { useLocale } from "@hooks";

type FinaliseOpportunityFormData = {
  issueId: string;
  weeks: Array<{
    week: Date;
    won: boolean;
  }>;
};

type Props = {
  open: boolean;
  issueId: string;
  type: "won" | "lost";
  onCancel: () => void;
};

export function FinaliseOpportunityFormModal(props: Props) {
  const { locale } = useLocale();
  const form = useForm<FinaliseOpportunityFormData>({
    defaultValues: {
      issueId: props.issueId,
      weeks: []
    }
  });

  const query = useGetOpportunityIssueWeeksQuery({
    variables: {
      issueId: props.issueId
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      form.reset({
        issueId: props.issueId,
        weeks: [
          ...data.consultantOpportunityIssueWeeks.openWeeks.map((week) => ({
            week: week,
            won: props.type === "won"
          })),
          ...data.consultantOpportunityIssueWeeks.wonWeeks.map((week) => ({
            week: week,
            won: true
          })),
          ...data.consultantOpportunityIssueWeeks.lostWeeks.map((week) => ({
            week: week,
            won: false
          }))
        ].sort((a, b) => new Date(a.week).getTime() - new Date(b.week).getTime())
      });
    },
    skip: !props.issueId
  });

  const [finaliseOpportunity] = useFinaliseOpportunityMutation({
    onCompleted: () => {
      props.onCancel();
    }
  });

  const submit = (data: FinaliseOpportunityFormData) => {
    return finaliseOpportunity({
      variables: {
        issueId: data.issueId,
        weeksWon: data.weeks.filter((w) => w.won).map((w) => w.week)
      }
    });
  };

  const weeks = form.watch("weeks");
  const isAllSelected = weeks.every((x) => x.won);

  const selectAll = () => {
    for (const week of weeks) {
      week.won = true;
    }
    form.setValue("weeks", weeks);
  };

  const unselectAll = () => {
    for (const week of weeks) {
      week.won = false;
    }
    form.setValue("weeks", weeks);
  };

  const working = query.loading || form.formState.isSubmitting;

  return (
    <form onSubmit={form.handleSubmit(submit)}>
      <Dialog open={props.open} maxWidth="sm" fullWidth onClose={props.onCancel}>
        <DialogTitle>Markera kort som bokad</DialogTitle>

        <DialogContent>
          {query.loading && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              minHeight="10rem"
            >
              <CircularProgress />
            </Box>
          )}

          {!query.loading && (
            <Box
              display="flex"
              flexDirection="column"
              gridGap="0.5rem"
              maxWidth="10rem"
              marginLeft="auto"
              marginRight="auto"
            >
              <Box display="flex" justifyContent="end">
                <Link
                  style={{ cursor: "pointer" }}
                  onClick={isAllSelected ? unselectAll : selectAll}
                >
                  <Typography variant="body2">
                    {isAllSelected ? locale.deselectAll : locale.selectAll}
                  </Typography>
                </Link>
              </Box>
              {weeks.map((week, index) => {
                const w = `v. ${moment(week.week).format("WW-GGGG")}`;

                return (
                  <Box
                    key={index}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2">{w}</Typography>

                    <Switch
                      {...form.register(`weeks.${index}.won`)}
                      checked={week.won}
                      disabled={working}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={props.onCancel} disabled={working}>
            {locale.Cancel}
          </Button>

          <Button
            color="primary"
            variant="contained"
            disabled={working}
            onClick={form.handleSubmit(submit)}
          >
            {locale.Save}
          </Button>
        </DialogActions>
      </Dialog>

      <DevTool control={form.control} />
    </form>
  );
}
